import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { PageLoader, ButtonLoader } from '../components/ui/loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Trash2, 
  Edit3, 
  Save,
  FileText,
  GripVertical,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'list', label: 'List/Array' },
  { value: 'boolean', label: 'Yes/No' },
];

export default function ParserTemplates() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formFields, setFormFields] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api().get('/parser-templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormFields([]);
    setEditingTemplate(null);
  };

  const openCreateDialog = () => {
    resetForm();
    // Add one default field
    setFormFields([{
      id: Date.now(),
      name: '',
      description: '',
      field_type: 'text',
      required: false,
      page_hint: ''
    }]);
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormName(template.name);
    setFormDescription(template.description || '');
    setFormFields(template.fields.map((f, i) => ({ ...f, id: Date.now() + i })));
    setDialogOpen(true);
  };

  const addField = () => {
    setFormFields([...formFields, {
      id: Date.now(),
      name: '',
      description: '',
      field_type: 'text',
      required: false,
      page_hint: ''
    }]);
  };

  const removeField = (fieldId) => {
    setFormFields(formFields.filter(f => f.id !== fieldId));
  };

  const updateField = (fieldId, key, value) => {
    setFormFields(formFields.map(f => 
      f.id === fieldId ? { ...f, [key]: value } : f
    ));
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    if (formFields.length === 0) {
      toast.error('At least one field is required');
      return;
    }
    
    const hasEmptyFieldName = formFields.some(f => !f.name.trim());
    if (hasEmptyFieldName) {
      toast.error('All fields must have a name');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formName,
        description: formDescription,
        fields: formFields.map(({ id, ...rest }) => rest)
      };

      if (editingTemplate) {
        await api().put(`/parser-templates/${editingTemplate.id}`, payload);
        toast.success('Template updated successfully');
      } else {
        await api().post('/parser-templates', payload);
        toast.success('Template created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api().delete(`/parser-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const duplicateTemplate = (template) => {
    setEditingTemplate(null);
    setFormName(`${template.name} (Copy)`);
    setFormDescription(template.description || '');
    setFormFields(template.fields.map((f, i) => ({ ...f, id: Date.now() + i })));
    setDialogOpen(true);
  };

  if (loading) {
    return <PageLoader text="Loading templates..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="parser-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Parser Templates</h1>
          <p className="text-muted-foreground">Define structure templates for AI-powered data extraction</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} data-testid="create-template-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Template' : 'Create Parser Template'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Template Info */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Template Name *</label>
                  <Input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g., Contract Document, Invoice, Report"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Textarea
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Describe what this template extracts..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Fields */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium">Structure Fields</label>
                  <Button variant="outline" size="sm" onClick={addField}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Field
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {formFields.map((field, index) => (
                    <Card key={field.id} className="bg-muted/30">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-4">
                          <div className="text-muted-foreground pt-2">
                            <GripVertical className="h-5 w-5" />
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Field Name *</label>
                                <Input
                                  value={field.name}
                                  onChange={(e) => updateField(field.id, 'name', e.target.value)}
                                  placeholder="e.g., Title, Introduction, Amount"
                                  data-testid={`field-name-${index}`}
                                />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Field Type</label>
                                <Select 
                                  value={field.field_type} 
                                  onValueChange={(v) => updateField(field.id, 'field_type', v)}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {FIELD_TYPES.map(type => (
                                      <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground mb-1 block">Description (AI extraction hint)</label>
                              <Input
                                value={field.description}
                                onChange={(e) => updateField(field.id, 'description', e.target.value)}
                                placeholder="e.g., The main title of the document, usually at the top"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Page Hint (optional)</label>
                                <Input
                                  value={field.page_hint}
                                  onChange={(e) => updateField(field.id, 'page_hint', e.target.value)}
                                  placeholder="e.g., page 1, first 2 pages, all pages"
                                />
                              </div>
                              <div className="flex items-end pb-2">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`required-${field.id}`}
                                    checked={field.required}
                                    onCheckedChange={(checked) => updateField(field.id, 'required', checked)}
                                  />
                                  <label htmlFor={`required-${field.id}`} className="text-sm cursor-pointer">
                                    Required field
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeField(field.id)}
                            disabled={formFields.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <ButtonLoader /> : <Save className="h-4 w-4 mr-2" />}
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates List */}
      {templates.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-['Manrope']">{template.name}</CardTitle>
                    {template.description && (
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                    )}
                  </div>
                  <Badge variant="outline">{template.fields?.length || 0} fields</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  {template.fields?.slice(0, 4).map((field, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <FileText className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">{field.name}</span>
                      <Badge variant="secondary" className="text-xs">{field.field_type}</Badge>
                      {field.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                    </div>
                  ))}
                  {template.fields?.length > 4 && (
                    <p className="text-xs text-muted-foreground">+{template.fields.length - 4} more fields</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(template)}>
                    <Edit3 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => duplicateTemplate(template)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(template.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No parser templates yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create a template to define your document structure</p>
            <Button className="mt-4" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Template
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
